import type { ApolloClient } from '@apollo/client';
import AsyncSearchableSelectFieldEntry from '@aurora/shared-client/components/form/AsyncSearchableSelectField/AsyncSearchableSelectFieldEntry';
import type { EndUserRouter } from '@aurora/shared-client/routes/useEndUserRoutes';
import type { RouteWithQuery } from '@aurora/shared-client/routes/useCustomRouter';
import type {
  StringMatchesConstraint,
  User
} from '@aurora/shared-generated/types/graphql-schema-types';
import { SortDirection } from '@aurora/shared-generated/types/graphql-schema-types';
import type { ContextUserFragment } from '@aurora/shared-generated/types/graphql-types';
import type { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { EndUserPages } from '@aurora/shared-types/pages/enums';
import { getLog } from '@aurora/shared-utils/log';
import type { UserViewsQuery, UserViewsQueryVariables } from '../../types/graphql-types';
import userViewsQuery from './UserViews.query.graphql';

const log = getLog(module);

export default async function fetchUserListBySearchTerm(
  inputValue: string,
  cursor: string,
  client: ApolloClient<unknown>,
  minInputLengthForSearch = 0,
  excludedUserList = null
): Promise<{
  options: AsyncSearchableSelectFieldEntry<User>[];
  hasMore: boolean;
  cursor: string;
}> {
  const matchTermClause: StringMatchesConstraint = {
    matches: inputValue.toLowerCase()
  };

  if (inputValue.length >= minInputLengthForSearch) {
    const { loading, data } = await client.query<UserViewsQuery, UserViewsQueryVariables>({
      query: userViewsQuery,
      variables: {
        constraints: {
          login: matchTermClause
        },
        sorts: { login: { direction: SortDirection.Asc } },
        first: 10,
        after: cursor,
        useAvatar: true,
        useRank: true,
        useBiography: true
      }
    });

    const includedUsers = excludedUserList
      ? data?.users.edges.filter(user => !excludedUserList.includes(user.node.id))
      : data?.users.edges;

    const recipients = loading
      ? []
      : includedUsers.map(
          user =>
            new AsyncSearchableSelectFieldEntry<User>(
              user.node.login,
              user.node.login,
              user.node as User
            )
        );

    const { endCursor, hasNextPage } = data.users?.pageInfo;
    return {
      options: recipients,
      hasMore: hasNextPage,
      cursor: endCursor
    };
  }
  return {
    options: [],
    hasMore: false,
    cursor: null
  };
}

/**
 * Returns whether we should redirect the user profile page, depending on the login id in query and context user.
 * If the login id is different in query and contextUser, redirect the page.
 *
 * @param contextUser The principal user
 * @param router Current app router
 * @param getCaseSensitiveLogin function to identify case sensitive login id
 *
 * @returns boolean whether to redirect the page.
 */
function shouldRedirectUserPage(
  contextUser: ContextUserFragment,
  router: EndUserRouter,
  getCaseSensitiveLogin: (value: string) => string
): boolean {
  if (!contextUser) {
    return false;
  }
  const isUserLoginMisMatch = getCaseSensitiveLogin(contextUser.login) !== router?.query?.login;
  if (isUserLoginMisMatch) {
    log.warn(`User redirection because login does not match.
    Context user login: ${contextUser.login} Query login: ${router?.query?.login}`);
  }
  return isUserLoginMisMatch;
}

/**
 * Returns the redirect rules for User profile page
 *
 * @param contextUser The principal user.
 * @param router Current app router.
 * @param getCaseSensitivePath function to identify case sensitive login id
 *
 * @returns Route and params for the redirect.
 */
function getRedirectRulesForUserPage(
  contextUser: ContextUserFragment,
  router: EndUserRouter,
  getCaseSensitivePath: (value: string) => string
): RouteWithQuery<EndUserPages, EndUserQueryParams> {
  if (contextUser) {
    const { login, uid } = contextUser;
    return {
      route: EndUserPages.UserPage,
      params: {
        login: getCaseSensitivePath(login),
        userId: uid
      }
    };
  }
  return {
    route: router.getCurrentPageName(),
    params: router.getPathParams()
  };
}

/**
 * Returns the redirect rules for User badges page
 *
 * @param contextUser The principal user.
 * @param router Current app router.
 * @param getCaseSensitivePath function to identify case sensitive login id
 *
 * @returns Route and params for the redirect.
 */
function getRedirectRulesForUserBadgesPage(
  contextUser: ContextUserFragment,
  router: EndUserRouter,
  getCaseSensitivePath: (value: string) => string
): RouteWithQuery<EndUserPages, EndUserQueryParams> {
  if (contextUser) {
    const { login, uid } = contextUser;
    return {
      route: EndUserPages.UserBadgesPage,
      params: {
        login: getCaseSensitivePath(login),
        userId: uid
      }
    };
  }
  return {
    route: router.getCurrentPageName(),
    params: router.getPathParams()
  };
}

export { shouldRedirectUserPage, getRedirectRulesForUserPage, getRedirectRulesForUserBadgesPage };
